import { useState, useEffect, useCallback, useRef } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import NotificationsPanel from "./NotificationsPanel";
import {
  LayoutDashboard,
  Settings,
  Users,
  BookOpen,
  Megaphone,
  DollarSign,
  BarChart3,
  GraduationCap,
  Briefcase,
  FlaskConical,
  Globe,
  Menu,
  X,
  LogOut,
  ChevronDown,
  Bell,
  Search,
  Shield,
  AlertCircle,
  Home,
  FolderOpen,
  PanelLeftClose,
  PanelLeft,
  Cpu,
} from "lucide-react";

const Layout = () => {
  // Load sidebar collapsed state from localStorage
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });
  
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [directionsOpen, setDirectionsOpen] = useState(true);
  const [divisionsOpen, setDivisionsOpen] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const lastUnreadCountRef = useRef(0);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Save sidebar collapsed state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const fetchNotifications = useCallback(async (options = {}) => {
    const { showBrowserNotificationIfNew = false } = options;
    try {
      const response = await axios.get(`${API}/notifications`, {
        withCredentials: true,
      });
      const list = response.data || [];
      setNotifications(list);
      const unreadCount = list.filter((n) => !n.read).length;
      // Mostrar notificación del navegador solo si este refetch fue por crear anuncio/aviso (no en carga inicial)
      if (showBrowserNotificationIfNew && typeof Notification !== "undefined" && Notification.permission === "granted" && unreadCount > lastUnreadCountRef.current) {
        const firstUnread = list.find((n) => !n.read);
        if (firstUnread) {
          try {
            new Notification("WCA – Nueva notificación", {
              body: firstUnread.title || firstUnread.message || "Tienes una nueva notificación",
              icon: "/favicon.ico",
            });
          } catch (_) {}
        }
      }
      lastUnreadCountRef.current = unreadCount;
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error("Error fetching notifications:", error);
      }
      setNotifications([]);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Refrescar notificaciones cuando se crea un anuncio/aviso (sin recargar página) y opcionalmente mostrar notificación del navegador
  useEffect(() => {
    const onRefresh = () => fetchNotifications({ showBrowserNotificationIfNew: true });
    window.addEventListener("app:refresh-notifications", onRefresh);
    return () => window.removeEventListener("app:refresh-notifications", onRefresh);
  }, [fetchNotifications]);

  // Track window width for responsive behavior
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);

  // Handle responsive sidebar
  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) {
        setSidebarOpen(true);
      } else {
        setSidebarOpen(false);
        setSidebarCollapsed(false);
      }
    };
    
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success("Sesión cerrada correctamente");
    navigate("/login");
  };

  const closeMobileSidebar = () => {
    if (window.innerWidth < 1024) {
      setSidebarOpen(false);
    }
  };

  const handleMarkAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/notifications/${notificationId}/read`, {}, { withCredentials: true });
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (e) {
      setNotifications(notifications.map(n =>
        n.id === notificationId ? { ...n, read: true } : n
      ));
    }
  };

  const handleClearAllNotifications = async () => {
    try {
      await axios.delete(`${API}/notifications/clear`, { withCredentials: true });
      setNotifications([]);
      setNotificationsOpen(false);
    } catch (e) {
      setNotifications([]);
      setNotificationsOpen(false);
    }
  };

  const unreadNotificationsCount = notifications.filter(n => !n.read).length;

  // All directions including Dirección General
  const allDirections = [
    { path: "/d-general", id: "general", label: "Dirección General", shortLabel: "Dirección General", icon: LayoutDashboard },
    { path: "/d-operaciones", id: "operaciones", label: "Dirección de Operaciones y Logística", shortLabel: "Operaciones", icon: Settings },
    { path: "/d-talento", id: "talento", label: "Dirección de Talento y Bienestar", shortLabel: "Talento", icon: Users },
    { path: "/d-academia", id: "academia", label: "Dirección Académica y de Innovación Educativa", shortLabel: "Academia", icon: BookOpen },
    { path: "/d-comunicacion", id: "comunicacion", label: "Dirección de Comunicación y Experiencia", shortLabel: "Comunic.", icon: Megaphone },
    { path: "/d-finanzas", id: "finanzas", label: "Dirección de Estrategia y Finanzas", shortLabel: "Finanzas", icon: DollarSign },
    { path: "/d-informacion", id: "informacion", label: "Dirección de Investigación e Información", shortLabel: "Información", icon: BarChart3 },
    { path: "/d-tecnologia", id: "tecnologia", label: "Dirección de Desarrollo Tecnológico e Innovación", shortLabel: "Tecnología", icon: Cpu },
  ];

  const allDivisions = [
    { path: "/wca-learn", id: "learn", label: "WCA | Learn", shortLabel: "Learn", icon: GraduationCap },
    { path: "/wca-network", id: "network", label: "WCA | Network", shortLabel: "Network", icon: Briefcase },
    { path: "/wca-labs", id: "labs", label: "WCA | Labs", shortLabel: "Labs", icon: FlaskConical },
    { path: "/wca-impact", id: "impact", label: "WCA | Impact", shortLabel: "Impact", icon: Globe },
  ];

  // Filter based on user permissions
  const isSuperAdmin = user?.is_super_admin || user?.role === "admin";
  
  const getAccessibleDirections = () => {
    if (isSuperAdmin) {
      return allDirections;
    }
    if (!user?.is_active) {
      return [];
    }
    const accessibleIds = user?.accessible_modules?.directions?.map(d => d.id) || user?.assigned_directions || [];
    return allDirections.filter(d => accessibleIds.includes(d.id));
  };

  const getAccessibleDivisions = () => {
    if (isSuperAdmin) {
      return allDivisions;
    }
    if (!user?.is_active) {
      return [];
    }
    const accessibleIds = user?.accessible_modules?.divisions?.map(d => d.id) || user?.assigned_divisions || [];
    return allDivisions.filter(d => accessibleIds.includes(d.id));
  };

  const directions = getAccessibleDirections();
  const divisions = getAccessibleDivisions();

  // Show pending activation message
  const showPendingMessage = !user?.is_active && !isSuperAdmin;

  const sidebarWidth = sidebarCollapsed ? "80px" : "320px";

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary-50/30 to-secondary-50/30">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 glass-panel rounded-none border-x-0 border-t-0">
        <div className="flex items-center justify-between p-4">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
            data-testid="mobile-menu-btn"
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center gap-2">
            <img
              src="/logo-wca.jpeg"
              alt="WCA Logo"
              className="w-8 h-8 rounded-lg object-contain"
            />
            <span className="font-bold text-slate-800">WCA</span>
          </div>
          <button 
            onClick={() => { fetchNotifications(); setNotificationsOpen(true); }}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors relative"
            data-testid="mobile-notifications-btn"
          >
            <Bell size={20} className="text-slate-600" />
            {unreadNotificationsCount > 0 && (
              <span className="absolute top-1 right-1 w-4 h-4 bg-rose-500 rounded-full text-[10px] text-white flex items-center justify-center">
                {unreadNotificationsCount}
              </span>
            )}
          </button>
        </div>
      </header>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && !isDesktop && (
        <div
          className="lg:hidden fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 bottom-0 left-0 z-50 glass-panel rounded-none lg:rounded-r-3xl overflow-hidden transition-all duration-300 flex flex-col ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } ${sidebarCollapsed ? "lg:w-20" : "w-72 lg:w-80"}`}
        style={{ width: window.innerWidth >= 1024 ? (sidebarCollapsed ? "80px" : "320px") : undefined }}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-4 lg:p-6 border-b border-slate-200/50">
          <div className="flex items-center justify-between">
            <div className={`flex items-center gap-3 ${sidebarCollapsed ? "justify-center w-full" : ""}`}>
              <div className={`flex items-center justify-center ${sidebarCollapsed ? "w-10 h-10 rounded-lg flex-shrink-0" : ""}`}>
                <img
                  src="/logo-wca.jpeg"
                  alt="WCA Logo"
                  className={`object-contain transition-all ${sidebarCollapsed ? "w-10 h-10 rounded-lg" : "w-10 h-10 lg:w-12 lg:h-12 rounded-xl"}`}
                />
              </div>
              {!sidebarCollapsed && (
                <div>
                  <p className="text-xs text-slate-500">Ecosistema</p>
                  <h1 className="font-bold text-slate-800 text-lg">WCA</h1>
                </div>
              )}
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Pending Activation Notice */}
        {showPendingMessage && !sidebarCollapsed && (
          <div className="mx-4 mt-4 p-3 rounded-xl bg-amber-50 border border-amber-200">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">Cuenta pendiente</p>
                <p className="text-xs text-amber-600 mt-1">
                  Contacta al administrador.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto sidebar-scroll p-4 space-y-4">
          {/* General Section - Always visible for active users */}
          {(user?.is_active || isSuperAdmin) && (
            <div>
              <NavLink
                to="/general"
                onClick={closeMobileSidebar}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? "active" : ""} ${sidebarCollapsed ? "justify-center px-2" : ""}`
                }
                data-testid="nav-general"
                title="Información General"
              >
                <Home size={20} className="flex-shrink-0" />
                {!sidebarCollapsed && <span className="truncate">Información General</span>}
              </NavLink>
              
              {/* Recursos Compartidos - visible for all active users */}
              <NavLink
                to="/recursos"
                onClick={closeMobileSidebar}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? "active" : ""} ${sidebarCollapsed ? "justify-center px-2" : ""}`
                }
                data-testid="nav-recursos-compartidos"
                title="Recursos Compartidos"
              >
                <FolderOpen size={20} className="flex-shrink-0" />
                {!sidebarCollapsed && <span className="truncate">Recursos Compartidos</span>}
              </NavLink>
            </div>
          )}

          {/* Admin Section */}
          {isSuperAdmin && (
            <div>
              {!sidebarCollapsed && (
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2">
                  Administración
                </p>
              )}
              <NavLink
                to="/admin"
                onClick={closeMobileSidebar}
                className={({ isActive }) =>
                  `sidebar-item ${isActive ? "active" : ""} ${sidebarCollapsed ? "justify-center px-2" : ""}`
                }
                data-testid="nav-admin"
                title="Panel de Administración"
              >
                <Shield size={20} className="flex-shrink-0" />
                {!sidebarCollapsed && <span className="truncate">Panel de Administración</span>}
              </NavLink>
            </div>
          )}

          {/* Direcciones Section */}
          {directions.length > 0 && (
            <div>
              {!sidebarCollapsed ? (
                <button
                  onClick={() => setDirectionsOpen(!directionsOpen)}
                  className="flex items-center justify-between w-full text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2"
                >
                  <span>Direcciones</span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${directionsOpen ? "rotate-0" : "-rotate-90"}`}
                  />
                </button>
              ) : (
                <div className="border-t border-slate-200/50 my-2"></div>
              )}
              {(directionsOpen || sidebarCollapsed) && (
                <div className="space-y-1">
                  {directions.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileSidebar}
                      className={({ isActive }) =>
                        `sidebar-item ${isActive ? "active" : ""} ${sidebarCollapsed ? "justify-center px-2" : ""}`
                      }
                      data-testid={`nav-${item.path.replace("/", "")}`}
                      title={item.label}
                    >
                      <item.icon size={20} className="flex-shrink-0" />
                      {!sidebarCollapsed && <span className="truncate text-sm">{item.label}</span>}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Divisiones Section */}
          {divisions.length > 0 && (
            <div>
              {!sidebarCollapsed ? (
                <button
                  onClick={() => setDivisionsOpen(!divisionsOpen)}
                  className="flex items-center justify-between w-full text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-2"
                >
                  <span>Divisiones</span>
                  <ChevronDown
                    size={14}
                    className={`transition-transform ${divisionsOpen ? "rotate-0" : "-rotate-90"}`}
                  />
                </button>
              ) : (
                <div className="border-t border-slate-200/50 my-2"></div>
              )}
              {(divisionsOpen || sidebarCollapsed) && (
                <div className="space-y-1">
                  {divisions.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileSidebar}
                      className={({ isActive }) =>
                        `sidebar-item ${isActive ? "active" : ""} ${sidebarCollapsed ? "justify-center px-2" : ""}`
                      }
                      data-testid={`nav-${item.path.replace("/", "")}`}
                      title={item.label}
                    >
                      <item.icon size={20} className="flex-shrink-0" />
                      {!sidebarCollapsed && <span className="truncate">{item.label}</span>}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        {/* Collapse Toggle - Desktop only */}
        <div className="hidden lg:block p-2 border-t border-slate-200/50">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full p-3 hover:bg-slate-100 rounded-xl transition-colors flex items-center gap-2 ${sidebarCollapsed ? "justify-center" : ""}`}
            title={sidebarCollapsed ? "Expandir menú" : "Contraer menú"}
            data-testid="toggle-sidebar-btn"
          >
            {sidebarCollapsed ? (
              <PanelLeft size={20} className="text-slate-500" />
            ) : (
              <>
                <PanelLeftClose size={20} className="text-slate-500" />
                <span className="text-sm text-slate-500">Contraer menú</span>
              </>
            )}
          </button>
        </div>

        {/* User Section */}
        <div className="p-4 border-t border-slate-200/50">
          <div className={`flex items-center gap-3 ${sidebarCollapsed ? "justify-center" : ""}`}>
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="text-primary font-semibold">
                  {user?.name?.charAt(0) || "U"}
                </span>
              </div>
            )}
            {!sidebarCollapsed && (
              <>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-slate-800 truncate text-sm">{user?.name}</p>
                    {user?.is_super_admin && (
                      <Shield size={12} className="text-primary flex-shrink-0" />
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{user?.email}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="p-2 hover:bg-rose-50 hover:text-rose-600 rounded-lg transition-colors flex-shrink-0"
                  data-testid="logout-btn"
                  title="Cerrar sesión"
                >
                  <LogOut size={18} />
                </button>
              </>
            )}
          </div>
          {sidebarCollapsed && (
            <button
              onClick={handleLogout}
              className="w-full mt-2 p-2 hover:bg-rose-50 hover:text-rose-600 rounded-lg transition-colors flex items-center justify-center"
              data-testid="logout-btn-collapsed"
              title="Cerrar sesión"
            >
              <LogOut size={18} />
            </button>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main 
        className={`pt-16 lg:pt-0 transition-all duration-300 ${
          isDesktop 
            ? (sidebarCollapsed ? "lg:ml-20" : "lg:ml-80") 
            : "ml-0"
        }`}
      >
        {/* Desktop Top Bar */}
        <header className="hidden lg:flex glass-panel m-6 mb-0 p-4 items-center justify-between" data-testid="header">
          <div className="flex items-center gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                placeholder="Buscar..."
                className="w-full glass-input pl-11"
                data-testid="search-input"
              />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => { fetchNotifications(); setNotificationsOpen(true); }}
              className="p-3 hover:bg-slate-100 rounded-xl transition-colors relative" 
              data-testid="notifications-btn"
            >
              <Bell size={20} className="text-slate-600" />
              {unreadNotificationsCount > 0 && (
                <span className="absolute top-1 right-1 w-5 h-5 bg-rose-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                  {unreadNotificationsCount}
                </span>
              )}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="p-4 lg:p-6 animate-fade-in">
          <Outlet />
        </div>
      </main>

      {/* Notifications Panel */}
      <NotificationsPanel
        isOpen={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
        notifications={notifications}
        onMarkAsRead={handleMarkAsRead}
        onClearAll={handleClearAllNotifications}
        onRequestBrowserNotifications={() => {
          if (typeof Notification !== "undefined" && Notification.permission === "default") {
            Notification.requestPermission().then(() => fetchNotifications());
          }
        }}
        browserNotificationPermission={typeof Notification !== "undefined" ? Notification.permission : "denied"}
      />
    </div>
  );
};

export default Layout;
